import {
  getDashboardAppScoutDashboardGetOptions,
  getRecommendationCountAppInboxRecommendationsCountGetOptions,
} from '@/client/@tanstack/react-query.gen'
import { DemodayWelcomeOverlay } from '@/components/demoday-welcome-overlay'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAnalytics } from '@/hooks/use-analytics'
import { useAuth } from '@/hooks/use-auth'
import { useTour } from '@/hooks/use-tour'
import { DEMODAY_WELCOME_KEY, DEMODAY_WELCOME_TTL_MS } from '@/lib/constants'
import { buildScoutHandoffSearch } from '@/routes/_authed/_user/inbox/-utils/scout-handoff'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AnimatePresence } from 'framer-motion'
import { ArrowRight, ArrowUpRight, BarChart3, Mail, MessageSquareText, UsersRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { HomePageSkeleton } from './-components/skeleton'

/**
 * Consume the one-shot demoday handoff flag at mount and return the
 * first-name to greet (or null if no fresh handoff is pending). Clears the
 * sessionStorage entry on read so a back-button trip to the dashboard
 * doesn't replay the animation.
 */
function consumeDemodayWelcome(): string | undefined {
  if (globalThis.window === undefined) {
    return undefined
  }
  // Dev-preview hatch: visit /?demoday_welcome=Anything to replay the
  // brand-takeover overlay against the real dashboard. The query string
  // is parsed inline so this works without touching the route's search-
  // param validator.
  const previewName = new URLSearchParams(globalThis.location.search).get(
    'demoday_welcome',
  )
  if (previewName) {
    return previewName
  }
  const raw = sessionStorage.getItem(DEMODAY_WELCOME_KEY)
  if (!raw) {
    return undefined
  }
  // Always consume — even a malformed value should not keep firing.
  sessionStorage.removeItem(DEMODAY_WELCOME_KEY)
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed !== null
      && typeof parsed === 'object'
      && 'firstName' in parsed
      && 'ts' in parsed
      && typeof parsed.firstName === 'string'
      && typeof parsed.ts === 'number'
      && Date.now() - parsed.ts <= DEMODAY_WELCOME_TTL_MS
    ) {
      return parsed.firstName
    }
  }
  catch {
    // Malformed JSON — treat as "no handoff". The key is already cleared.
  }
  return undefined
}

export const Route = createFileRoute('/_authed/_user/')({
  component: Home,
  pendingComponent: HomePageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Home',
  }),
})

function Home() {
  const { user, canUseScoutLeads, isOrgAdmin, isLoading, organizationId } = useAuth()
  const { capture } = useAnalytics()
  const scoutDashboard = useQuery({
    ...getDashboardAppScoutDashboardGetOptions(),
    enabled: !!organizationId && canUseScoutLeads,
  })
  const inboxCounts = useQuery({
    ...getRecommendationCountAppInboxRecommendationsCountGetOptions(),
    enabled: !!organizationId,
  })

  const firstName = user?.first_name ?? 'Scout User'
  const pregeneratedCount = scoutDashboard.data?.pregeneratedCount ?? 0
  const scoutLeadsItemPublicId = scoutDashboard.data?.scoutLeadsItemPublicId ?? undefined
  const unreadCount = inboxCounts.data?.unread ?? 0
  let leadsBadgeLabel = 'Coming soon'
  if (canUseScoutLeads && pregeneratedCount > 0) {
    leadsBadgeLabel = `${pregeneratedCount} available`
  }
  else if (canUseScoutLeads && !isOrgAdmin) {
    leadsBadgeLabel = 'Admin only'
  }

  // Pick up the one-shot demoday handoff flag on first paint. If present,
  // render the brand-takeover overlay over the dashboard until its
  // animation completes, then unmount it (with a slow fade) and launch the
  // demoday orientation tour over the freshly-revealed dashboard.
  const [demodayWelcomeName, setDemodayWelcomeName] = useState<string | undefined>(
    () => consumeDemodayWelcome(),
  )
  const { startTour } = useTour()

  const handleDemodayOverlayComplete = () => {
    // Drop the overlay (AnimatePresence handles the ~1.2 s exit fade) and
    // start the orientation tour at the same time. The tour's first step
    // popover renders while the overlay is still fading away, which feels
    // like a smooth handoff rather than a hard cut.
    setDemodayWelcomeName(undefined)
    startTour('demoday-orientation')
  }

  // Fire once per mount, after auth has settled, so scout_admin_card_visible
  // reflects the resolved entitlements rather than the initial empty state.
  const viewedRef = useRef(false)
  useEffect(() => {
    if (viewedRef.current || isLoading)
      return
    viewedRef.current = true
    capture('home_viewed', {
      scout_admin_card_visible: !!(canUseScoutLeads && isOrgAdmin),
    })
  }, [capture, isLoading, canUseScoutLeads, isOrgAdmin])

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 lg:px-6">
      <AnimatePresence>
        {demodayWelcomeName !== undefined && (
          <DemodayWelcomeOverlay
            firstName={demodayWelcomeName}
            onComplete={handleDemodayOverlayComplete}
          />
        )}
      </AnimatePresence>
      <header className="mx-auto max-w-3xl space-y-2 text-center">
        <h1 className="text-2xl font-bold text-foreground md:text-3xl" data-tour="dashboard-greeting">
          Hey
          {' '}
          {firstName}
          , how can I help you today?
        </h1>
        <p className="text-muted-foreground">
          Your command center for accounts, leads, research, and activity.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-label="Home actions">
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-start gap-4">
              <UsersRound className="shrink-0 text-muted-foreground" />
              <CardTitle>View scored accounts</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start gap-4 md:flex-row md:items-end md:justify-between">
              <CardDescription>
                Instantly see top accounts and buying signals identified by Scout.
                Prioritize outreach with AI-scored leads.
              </CardDescription>
              <Button asChild>
                <Link
                  to="/accounts"
                  data-tour="home-view-accounts"
                  onClick={() => capture('home_quick_nav_clicked', { destination: 'accounts' })}
                >
                  View Accounts
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-start gap-4">
              <ArrowUpRight className="shrink-0 text-muted-foreground" />
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <CardTitle>Get new leads</CardTitle>
                <CountBadge>{leadsBadgeLabel}</CountBadge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col items-start">
            <CardDescription className="mb-4 flex-1">
              {canUseScoutLeads
                ? 'Pre-generated leads ready to claim and start prospecting.'
                : 'AI-powered lead prospecting finds new accounts that match your ideal customer profile.'}
            </CardDescription>
            {canUseScoutLeads && isOrgAdmin
              ? (
                  <Button asChild>
                    <Link
                      to="/inbox"
                      search={buildScoutHandoffSearch(scoutLeadsItemPublicId)}
                      onClick={() => capture('home_quick_nav_clicked', { destination: 'scout_leads' })}
                    >
                      Claim leads
                      <ArrowRight />
                    </Link>
                  </Button>
                )
              : (
                  <Button disabled>
                    {canUseScoutLeads ? 'Admin only' : 'Coming soon'}
                  </Button>
                )}
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-start gap-4">
              <MessageSquareText className="shrink-0 text-muted-foreground" />
              <CardTitle>Market research</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col items-start">
            <CardDescription className="mb-4 flex-1">
              AI-generated research for each industry segment.
            </CardDescription>
            <Button asChild>
              <Link
                to="/market-research"
                data-tour="home-market-research"
                onClick={() => capture('home_quick_nav_clicked', { destination: 'market_research' })}
              >
                View Reports
                <ArrowRight />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-start gap-4">
              <Mail className="shrink-0 text-muted-foreground" />
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <CardTitle>Inbox</CardTitle>
                {unreadCount > 0 && (
                  <CountBadge>
                    {unreadCount}
                    {' '}
                    new
                  </CountBadge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col items-start">
            <CardDescription className="mb-4 flex-1">
              {unreadCount > 0
                ? `${unreadCount} new action${unreadCount === 1 ? '' : 's'} since you last left. Review and respond to updates.`
                : 'Review and respond to account updates, lead batches, and recommended next steps.'}
            </CardDescription>
            <Button asChild>
              <Link
                to="/inbox"
                onClick={() => capture('home_quick_nav_clicked', { destination: 'inbox' })}
              >
                Go to Inbox
                <ArrowRight />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-start gap-4">
              <BarChart3 className="shrink-0 text-muted-foreground" />
              <CardTitle>Activity</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col items-start">
            <CardDescription className="mb-4 flex-1">
              Track team activity, recent changes, and engagement across your accounts.
            </CardDescription>
            <Button asChild>
              <Link
                to="/activity"
                onClick={() => capture('home_quick_nav_clicked', { destination: 'activity' })}
              >
                View Activity
                <ArrowRight />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}

function CountBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="outline">
      {children}
    </Badge>
  )
}
